import { z } from 'zod';

/* The wire contract, as Zod schemas rather than bare types.
 *
 * These are the schemas the BFF will validate against. Keeping them in Zod
 * means they can move to a package shared with the BFF and be the single
 * definition of a tariff, a connector type or a station status — every one of
 * which drifted at least once while these screens were prototyped. */

export const ChargePointStatus = z.enum(['available', 'in_use', 'offline']);
export type ChargePointStatus = z.infer<typeof ChargePointStatus>;

/*
 * A connector's state, in the charger's own vocabulary.
 *
 * OCPP 1.6 defines nine StatusNotification states and this used to keep four,
 * folding the other five into 'offline'. That is how a charge point sitting at
 * `Preparing` — plugged in, waiting to authorise, and heartbeating every sixty
 * seconds — rendered as an offline bay on a station labelled Unavailable, while
 * the header counted it as online. The operator could see that something was
 * online and could not find it anywhere.
 *
 * So the states are kept apart, and 'offline' now means one thing only: we have
 * no current word from this bay. It is not something a charger ever reports.
 */
export const ConnectorStatus = z.enum([
  'available',   // Available    — idle and ready
  'preparing',   // Preparing    — plugged in, waiting to start
  'charging',    // Charging     — delivering energy
  'suspended',   // SuspendedEV / SuspendedEVSE — session up, no energy moving
  'finishing',   // Finishing    — session done, cable still in
  'reserved',    // Reserved     — held for a driver
  'unavailable', // Unavailable  — the charger says it is out of service
  'faulted',     // Faulted      — the charger reports a fault
  'offline',     // no word from this bay at all
]);
export type ConnectorStatus = z.infer<typeof ConnectorStatus>;

export const Connector = z.object({
  /** The OCPP connectorId — the number the bay's QR resolves to and the one
   *  that appears in StartTransaction. Not a display index. */
  id: z.number().int().positive(),
  /* Null where the charger has not said. OCPP 1.6 carries type and power in
     StatusNotification only as optional fields and plenty of firmware sends
     neither, so these are genuinely unknown rather than defaultable — and a
     bay drawn as "1 kW · AC" because nobody knew is worse than one that says
     so. The operator can answer them per connector. */
  type: z.string().nullable(),
  powerKw: z.number().positive().nullable(),
  current: z.enum(['AC', 'DC']).nullable(),
  status: ConnectorStatus,
  /** What the operator calls this bay, if anything. */
  label: z.string().nullable().optional(),
  /** False when only we know about it — declared by an operator, never
   *  mentioned by the charger. */
  reported: z.boolean().optional(),
});
export type Connector = z.infer<typeof Connector>;

export const ChargePoint = z.object({
  /** The OCPP identity the unit announces from firmware. This is the key
   *  everywhere: the CSMS row id is an autoincrement that means nothing to
   *  anyone holding the hardware, and a unit that has been pre-registered but
   *  never connected has no CSMS row at all. */
  id: z.string(),
  name: z.string().min(1),
  status: ChargePointStatus,
  /** The site it stands on. Null is honest — a charge point that has connected
   *  but has not been placed yet is exactly the state worth seeing. */
  station: z.object({
    id: z.string(),
    name: z.string(),
    city: z.string().nullable(),
    address: z.string().nullable(),
    lat: z.number().nullable(),
    lng: z.number().nullable(),
  }).nullable().optional(),
  /** Recorded before the hardware was flashed, so the CSMS has never heard of
   *  it. The override row is the whole record of it until it connects. */
  preRegistered: z.boolean().optional(),
  /** Whether drivers can see this charge point. Defaults to test: a charger
   *  registers itself the moment it connects, and nothing reaches the app until
   *  an operator says it is real. */
  environment: z.enum(['test', 'prod']).default('test'),
  /** The CSMS row id. Not the key — but transactions are recorded against it,
   *  so joining a session back to a charge point still needs it. Null for a
   *  pre-registered unit, which by definition has no sessions. */
  csmsId: z.number().nullable().optional(),
  /* The operator's value where one is recorded, the charger's own report
     otherwise. OCPP 1.6 caps vendor and model at 20 characters, so a unit
     abbreviates — "YC" for YCL — and an operator can correct it. */
  vendor: z.string().nullable().optional(),
  model: z.string().nullable().optional(),
  serialNumber: z.string().nullable().optional(),
  /** Whether this model's connector status means anything about session
   *  state. False for hardware that reports a connector Available while a
   *  transaction is open on it — where an open session IS the session, and
   *  offering to close it would end a live charge. */
  trustsConnectorStatus: z.boolean().optional(),
  /** What the unit itself said, so the editor can show what is being
   *  corrected rather than silently replacing it. */
  reported: z.object({
    vendor: z.string().nullable(),
    model: z.string().nullable(),
    serialNumber: z.string().nullable(),
  }).optional(),
  firmware: z.string().nullable().optional(),
  /** Price per kWh in the station's currency. */
  price: z.number().nonnegative(),
  currency: z.string().length(3).optional(),
  /** How many connectors this charge point can run at once. Lower than the
   *  connector count on a unit whose guns share a power module. */
  maxSimultaneous: z.number().int().positive(),
  blocked: z.boolean().default(false),
  blockReason: z.string().optional(),
  blockedBy: z.string().optional(),
  blockedAt: z.string().optional(),
  /* Whether the charger is actually talking to us.
     'never'  — has never sent a message
     'stale'  — the CSMS still flags it online but it has gone silent
     'online' — heard from within the timeout
     Not the same as `isOnline`, which is set on connect and only cleared on a
     clean disconnect, so it sticks at true after any ungraceful drop. */
  /** The name the charger announces from firmware on every connect. This is
   *  its identity to the CSMS and is not ours to change — renaming edits the
   *  display name laid over it, never this. */
  chargePointId: z.string().nullable().optional(),
  presence: z.enum(['online', 'offline', 'stale', 'never']).optional(),
  /** Seconds since the last message, null if there has never been one. */
  silentFor: z.number().nullable().optional(),

  /* Where the charge point connects from. Not the site's map coordinates —
     those come from the CSMS location record and are often unset — but the
     address the hardware actually reaches us on, which is the one that answers
     "is this unit where we think it is?" */
  origin: z.object({
    ip: z.string(),
    ipLastSeen: z.string(),
    connections: z.number(),
    country: z.string().nullable(),
    countryCode: z.string().nullable(),
    district: z.string().nullable(),
    city: z.string().nullable(),
    notFound: z.boolean(),
  }).optional(),
  connectors: z.array(Connector),
  /** Sessions open on this unit right now. */
  activeTransactions: z.array(z.object({
    transactionId: z.string(),
    connectorId: z.number().nullable(),
    startTime: z.string().nullable(),
    chargingState: z.string().nullable(),
    totalKwh: z.union([z.string(), z.number()]).nullable(),
    meterAt: z.string().nullable().optional(),
  })).optional(),
  lastStop: z.object({
    transactionId: z.string(),
    reason: z.string().nullable(),
    at: z.string().nullable(),
    kwh: z.number().nullable(),
  }).nullable().optional(),
  lastStart: z.object({
    at: z.string().nullable(),
    status: z.string().nullable(),
    transactionId: z.string().nullable(),
    idTag: z.string().nullable(),
    accepted: z.boolean(),
  }).nullable().optional(),
});
export type ChargePoint = z.infer<typeof ChargePoint>;

/**
 * A station: the site, not the hardware.
 *
 * One address, one set of coordinates, one or more charge points standing on
 * it. Nothing creates one except an operator — a charge point registers itself
 * the instant it opens a websocket, which is why five connectivity probes are
 * on the network as real units, but the site under them only exists because
 * somebody said so.
 */
export const Station = z.object({
  id: z.string(),
  name: z.string().min(1),
  address: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  timezone: z.string(),
  notes: z.string().nullable(),
  /** How many charge points are assigned here. */
  chargePoints: z.number().int().nonnegative().optional(),
  createdBy: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedBy: z.string().nullable().optional(),
  updatedAt: z.string().optional(),
});
export type Station = z.infer<typeof Station>;

/** "Dhaka, Bangladesh" — or an honest reason there is no answer. */
export function placeOf(o: ChargePoint['origin']): string {
  if (!o) return '';
  if (o.notFound) return 'no location for this address';
  if (!o.country) return 'not looked up yet';
  return [o.city, o.district, o.country].filter(Boolean).join(', ');
}

export const AgentApproval = z.enum(['pending', 'approved', 'declined']);
export type AgentApproval = z.infer<typeof AgentApproval>;

export const Agent = z.object({
  id: z.string(),
  name: z.string().min(1),
  area: z.string(),
  approval: AgentApproval,
  availability: z.enum(['Available', 'Busy', 'Offline']),
  /** Commission is either a percentage or a flat fee per top-up. */
  commissionMode: z.enum(['percent', 'fixed']).nullable().optional(),
  commissionPct: z.number().nonnegative().nullable().optional(),
  commissionFixed: z.coerce.number().nonnegative().nullable().optional(),
  declineReason: z.string().nullable().optional(),
  /** What a declined counter wrote when it asked to be looked at again. */
  appealNote: z.string().nullable().optional(),
  appealedAt: z.string().nullable().optional(),
  appealAnsweredAt: z.string().nullable().optional(),
  /** Applied from the Spark Agent app rather than typed in here. */
  selfRegistered: z.boolean().optional(),
  emailVerified: z.boolean().optional(),
  decidedBy: z.string().nullable().optional(),
  decidedAt: z.string().nullable().optional(),
  appliedAt: z.string(),
  /* Spark Agent (migration 012). Optional: an older BFF does not send them. */
  counterName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  /** What the counter QR encodes, as https://ev.nexdecade.com/a/<code>. */
  counterCode: z.string().nullable().optional(),
  onShift: z.boolean().optional(),
  city: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  lat: z.number().nullable().optional(),
  lng: z.number().nullable().optional(),
  hasLogin: z.boolean().optional(),
  mustChangePassword: z.boolean().optional(),
  lastLoginAt: z.string().nullable().optional(),
  /** The Counter Terms version agreed to, and when (migration 050). */
  termsVersion: z.number().nullable().optional(),
  termsAcceptedAt: z.string().nullable().optional(),
  /** Set when the counter deleted its own account from Spark Agent. */
  closedAt: z.string().nullable().optional(),
});
export type Agent = z.infer<typeof Agent>;

export const ActivityEntry = z.object({
  /** A bigserial. It arrives as a string, because a 64-bit integer does not
   *  survive a round trip through a JavaScript number. */
  id: z.string(),
  at: z.string(),
  actor: z.string(),
  role: z.string(),
  /* Must hold every area the server's own check constraint allows, or a row
     arrives with an area this screen has no label for and shows a blank pill.
     `auth` was missing that way: production holds 85 of them. */
  area: z.enum(['station', 'charger', 'agent', 'release', 'access', 'money',
    'settings', 'dispute', 'auth', 'driver']),
  action: z.string(),
  target: z.string(),
  detail: z.string(),
});
export type ActivityEntry = z.infer<typeof ActivityEntry>;

/* A frame exactly as the CSMS recorded it.
 *
 * `raw` is the literal array that crossed the socket. It is carried through
 * untouched because when an operator is chasing a firmware quirk, a prettified
 * view of what we think the charger meant is the thing that hides the bug. */
export const OcppFrame = z.object({
  id: z.number(),
  timestamp: z.string(),
  chargePointId: z.string(),
  stationName: z.string(),
  /** 'cs' — the charger is talking. 'csms' — we are. */
  origin: z.enum(['cs', 'csms']),
  protocol: z.string().nullable(),
  action: z.string().nullable(),
  uniqueId: z.string().nullable(),
  /** 2 CALL, 3 CALLRESULT, 4 CALLERROR. */
  type: z.number(),
  payload: z.unknown(),
  raw: z.string().nullable(),
});
export type OcppFrame = z.infer<typeof OcppFrame>;

/** Charger → CSMS is the direction operators scan for first. */
export const frameDirection = (f: OcppFrame): 'in' | 'out' =>
  (f.origin === 'cs' ? 'in' : 'out');

export const MESSAGE_TYPE: Record<number, string> = {
  2: 'CALL', 3: 'RESULT', 4: 'ERROR',
};

/**
 * A one-line reading of a frame.
 *
 * Derived from the payload rather than stored, and deliberately narrow: it
 * names the few fields an operator scans a log for, and otherwise says nothing
 * instead of guessing. The raw array is one click away for everything else.
 */
export function frameSummary(f: OcppFrame): string {
  const p = (f.payload ?? {}) as Record<string, unknown>;
  const s = (k: string) => (p[k] === undefined || p[k] === null ? '' : String(p[k]));

  if (f.type === 4) return [s('errorCode'), s('errorDescription')].filter(Boolean).join(' · ');

  switch (f.action) {
    case 'BootNotification':
      return s('status')
        ? `${s('status')}${s('interval') ? `, heartbeat ${s('interval')}s` : ''}`
        : [s('chargePointVendor'), s('chargePointModel'), s('firmwareVersion')].filter(Boolean).join(' · ');
    case 'StatusNotification': {
      const err = s('errorCode');
      return [
        s('connectorId') && `c${s('connectorId')}`,
        s('status'),
        err && err !== 'NoError' ? err : '',
      ].filter(Boolean).join(' · ');
    }
    case 'Authorize':
    case 'StartTransaction':
    case 'StopTransaction': {
      const info = p.idTagInfo as { status?: string } | undefined;
      return [
        s('idTag'), info?.status,
        s('transactionId') && `tx ${s('transactionId')}`,
        s('meterStart') && `${s('meterStart')} Wh start`,
        s('meterStop') && `${s('meterStop')} Wh stop`,
        s('reason'),
      ].filter(Boolean).join(' · ');
    }
    case 'MeterValues': {
      const mv = p.meterValue as Array<{ sampledValue?: Array<Record<string, string>> }> | undefined;
      const sv = mv?.[0]?.sampledValue ?? [];
      const energy = sv.find((v) => (v.measurand ?? 'Energy.Active.Import.Register').startsWith('Energy'));
      const power = sv.find((v) => (v.measurand ?? '').startsWith('Power'));
      return [
        energy && `${energy.value} ${energy.unit ?? 'Wh'}`,
        power && `${power.value} ${power.unit ?? 'W'}`,
        s('transactionId') && `tx ${s('transactionId')}`,
      ].filter(Boolean).join(' · ');
    }
    case 'Heartbeat':
      return s('currentTime') ? new Date(s('currentTime')).toLocaleTimeString() : '';
    default:
      return s('status') || s('connectorId') ? [s('connectorId') && `c${s('connectorId')}`, s('status')].filter(Boolean).join(' · ') : '';
  }
}

export const AppRelease = z.object({
  /** Which app. Absent from a BFF older than migration 014: that is the driver app. */
  app: z.enum(['driver', 'agent']).default('driver'),
  platform: z.enum(['android', 'ios']),
  /** Where an uploaded build is served. Null means the `app.android_url` config row. */
  downloadUrl: z.string().nullable().optional(),
  displayName: z.string(),
  packageName: z.string(),
  latest: z.string(),
  build: z.number().int().positive(),
  minSupported: z.string(),
  forceUpdate: z.boolean(),
  rollout: z.number().int().min(0).max(100),
  published: z.boolean(),
  notes: z.string(),
  artifactName: z.string().nullable(),
  artifactSize: z.string().nullable(),
  artifactSha256: z.string().nullable(),
  updatedAt: z.string().nullable(),
  updatedBy: z.string().nullable(),
});
export type AppRelease = z.infer<typeof AppRelease>;

export const Role = z.enum(['superadmin', 'operator', 'support']);
export type Role = z.infer<typeof Role>;

export interface AdminUser {
  name: string;
  email: string;
  role: Role;
}

/* ------------------------------------------------------------- derived --- */

/** Connectors reporting available. */
export function idleCount(s: ChargePoint): number {
  return s.connectors.filter((c) => c.status === 'available').length;
}

/** Connectors currently delivering energy. */
export function chargingCount(s: ChargePoint): number {
  return s.connectors.filter((c) => c.status === 'charging').length;
}

/** States in which a driver is already at the bay, whether or not energy is
 *  moving. A cable is in it, so nobody else can take it. */
export const OCCUPIED_STATUSES: ConnectorStatus[] =
  ['preparing', 'charging', 'suspended', 'finishing'];

/** Bays with a driver on them. Wider than `chargingCount` on purpose: a
 *  connector at `Preparing` is taken, and calling that station Unavailable
 *  rather than In use describes the hardware instead of the situation. */
export function occupiedCount(s: ChargePoint): number {
  return s.connectors.filter((c) => OCCUPIED_STATUSES.includes(c.status)).length;
}

/**
 * One word per connector state, used on every screen that shows one.
 *
 * The same rule as `chargePointLabel`: the console has one vocabulary, so a bay
 * cannot read Preparing on the Chargers table and Offline on the station card.
 */
export const CONNECTOR_LABEL: Record<ConnectorStatus, string> = {
  available: 'Available',
  preparing: 'Preparing',
  charging: 'Charging',
  suspended: 'Suspended',
  finishing: 'Finishing',
  reserved: 'Reserved',
  unavailable: 'Out of service',
  faulted: 'Faulted',
  offline: 'Offline',
};

/** Green only where a driver could plug in right now; red only where the bay
 *  is not going to work. Everything in between is amber — busy is not broken. */
export function connectorTone(s: ConnectorStatus): 'on' | 'mid' | 'off' {
  if (s === 'available') return 'on';
  if (s === 'faulted' || s === 'unavailable' || s === 'offline') return 'off';
  return 'mid';
}

/**
 * Bays a driver can actually start on.
 *
 * Not the same as the idle count: on a unit whose guns share one power module,
 * an idle gun is unusable while its sibling charges. Counting it is how a
 * driver ends up at a charger that refuses them — so the badge, the filters
 * and the Start button all read this, never `status`.
 */
export function freeNow(s: ChargePoint): number {
  if (s.blocked || s.status === 'offline') return 0;
  const headroom = s.maxSimultaneous - chargingCount(s);
  if (headroom <= 0) return 0;
  return Math.min(idleCount(s), headroom);
}

/** True when the guns cannot all run together. */
export function sharesPower(s: ChargePoint): boolean {
  return s.maxSimultaneous < s.connectors.length;
}

/** Idle, but held back by the shared power module. */
export function isWaiting(s: ChargePoint, c: Connector): boolean {
  if (c.status !== 'available' || s.blocked) return false;
  const idle = s.connectors.filter((x) => x.status === 'available');
  return idle.indexOf(c) >= freeNow(s);
}

/** Peak this unit can deliver, across the bays that have told us. Null when
 *  none of them has — which is not the same as zero kilowatts. */
export function maxKw(s: ChargePoint): number | null {
  const known = s.connectors.map((c) => c.powerKw).filter((k): k is number => k != null);
  return known.length ? Math.max(...known) : null;
}

/**
 * One word for what an operator can do with this charge point, used everywhere.
 *
 * "Online" was the wrong word and it appeared on the Stations cards while the
 * Dashboard said "Available" for the same station. Online describes a socket;
 * the operator is asking whether a driver can plug in. Those differ — a charger
 * with a live connection and no free connector is online and not available.
 */
export function chargePointLabel(s: ChargePoint): string {
  if (s.blocked) return 'Blocked';
  if (s.presence === 'never') return 'Never connected';
  if (s.presence === 'stale') return 'Not responding';
  if (s.status === 'offline') return 'Offline';
  if (!s.connectors.length) return 'No connectors';
  // Occupied, not charging: a bay at Preparing is in use by someone standing
  // in front of it, and reading Unavailable there sends an operator chasing a
  // fault that is not happening.
  if (freeNow(s) === 0) return occupiedCount(s) ? 'In use' : 'Unavailable';
  return 'Available';
}

/** Green only when a driver could actually start a session. */
export function chargePointTone(s: ChargePoint): 'on' | 'mid' | 'off' {
  if (s.blocked || s.presence === 'never' || s.presence === 'stale') return 'off';
  if (s.status === 'offline' || !s.connectors.length) return 'off';
  return freeNow(s) > 0 ? 'on' : 'mid';
}

/** Why a charge point is not usable, in a sentence, for the row's subtitle. */
export function presenceNote(s: ChargePoint): string {
  if (s.presence === 'never') return 'has never sent a message to the CSMS';
  if (s.presence === 'offline' && s.silentFor) {
    return `last heard from ${ago(s.silentFor)} ago`;
  }
  if (s.presence === 'stale') {
    return `the CSMS flags this online, but it has been silent for ${ago(s.silentFor ?? 0)}`;
  }
  return '';
}

const ago = (sec: number): string =>
  (sec > 86_400 ? `${Math.round(sec / 86_400)}d`
    : sec > 3600 ? `${Math.round(sec / 3600)}h`
      : `${Math.max(1, Math.round(sec / 60))}m`);

export const ROLE_LABEL: Record<Role, string> = {
  superadmin: 'Super Admin',
  operator: 'Operator',
  support: 'Support',
};

/**
 * Which screens each role may reach — for the matrix on Users only.
 *
 * A copy of ROLE_SCREENS in spark_ev_bff/src/auth.ts, which is the real check
 * and also what drives the nav (the sign-in response carries `screens`). This
 * copy drifted badly once: it kept the old 'chargers' key, left operators
 * without 'drivers' and superadmins without 'topups', and had no rows for
 * charge points, connectors, the test bench or top-ups. Change both together.
 */
export const ROLE_NAV: Record<Role, string[]> = {
  superadmin: [
    'dashboard', 'stations', 'chargepoints', 'connectors', 'testbench', 'ocpp',
    'drivers', 'agents', 'transactions', 'topups', 'tariffs', 'disputes',
    'reports', 'users', 'releases', 'maps', 'activity',
  ],
  operator: [
    'dashboard', 'stations', 'chargepoints', 'connectors', 'testbench', 'ocpp',
    'drivers', 'agents', 'transactions', 'topups', 'tariffs', 'disputes',
    'reports', 'activity',
  ],
  support: ['dashboard', 'drivers', 'transactions', 'disputes'],
};

export function can(role: Role, screen: string): boolean {
  return ROLE_NAV[role].includes(screen);
}
